// Twee percentages op projectniveau (SPEC 27okt 3), intern: % op de totale offertewaarde en % op het
// totaal gefactureerd, naast elkaar. Absolute bedragen bij hover of in de detailregel. Nog niets
// gefactureerd: "nog niet gefactureerd". Verschil > 5 procentpunt: kleine waarschuwing.
import type { TweePercentages as TweePercentagesData } from '@recotex/shared';
import { PERCENTAGE_VERSCHIL_DREMPEL_PP } from '@recotex/shared';
import { arbeidskostTone, fmtEUR } from '../lib/format';

function pctTekst(n: number): string {
  return n.toFixed(2).replace('.', ',') + '%';
}

export function TweePercentages({ data, compact = false, detail = false }: {
  data: TweePercentagesData | null | undefined;
  /** Kaartweergave: kleiner, zonder labels in volle tekst. */
  compact?: boolean;
  /** Detailregel met de absolute bedragen onder de percentages. */
  detail?: boolean;
}) {
  if (!data) return <span className="text-secondary text-sm">—</span>;
  const kostTitel = `Kost onderaannemer ${fmtEUR(data.kost)}${data.kostOpen > 0 ? `, waarvan ${fmtEUR(data.kostOpen)} open voor oplevering` : ''}`;
  const voorlopig = data.voorlopig ? ' italic underline decoration-dotted underline-offset-4' : '';
  const label = (lang: string, kort: string) => <span className="text-xs font-medium text-secondary">{compact ? kort : lang}</span>;
  return (
    <div className={compact ? 'flex items-baseline gap-3 flex-wrap' : 'space-y-1'}>
      <div className={compact ? 'flex items-baseline gap-x-3 gap-y-1 flex-wrap' : 'flex items-baseline gap-4 flex-wrap'}>
        <span
          className="inline-flex items-baseline gap-1"
          title={data.offertewaarde != null ? `${kostTitel} / offertewaarde ${fmtEUR(data.offertewaarde)} (aanvaarde offertes, excl. btw)` : 'Geen offertewaarde bekend'}
        >
          {label('op offertewaarde', 'offerte')}
          {data.opOfferte != null
            ? <span className={`font-bold tabular-nums ${arbeidskostTone(data.opOfferte)}${voorlopig}`}>{pctTekst(data.opOfferte)}</span>
            : <span className="text-secondary text-sm">—</span>}
        </span>
        <span
          className="inline-flex items-baseline gap-1"
          title={data.gefactureerd != null ? `${kostTitel} / gefactureerd ${fmtEUR(data.gefactureerd)} (verkoopfacturen min creditnota's, excl. btw)` : 'Nog niets gefactureerd aan de klant'}
        >
          {label('op gefactureerd', 'gefact.')}
          {data.opGefactureerd != null
            ? <span className={`font-bold tabular-nums ${arbeidskostTone(data.opGefactureerd)}${voorlopig}`}>{pctTekst(data.opGefactureerd)}</span>
            : <span className="text-secondary text-xs italic">nog niet gefactureerd</span>}
        </span>
        {data.waarschuwing && (
          <span
            className="chip-warning"
            title={`Verschil van ${data.verschilPp!.toFixed(1).replace('.', ',')} procentpunt (> ${PERCENTAGE_VERSCHIL_DREMPEL_PP}): onvolledige facturatie of meerwerk dat niet gefactureerd is.`}
          >
            ⚠ {data.verschilPp!.toFixed(1).replace('.', ',')} pp verschil
          </span>
        )}
      </div>
      {detail && (
        <p className="text-xs text-secondary normal-case tracking-normal">
          Kost {fmtEUR(data.kost)}
          {data.kostOpen > 0 && <> (waarvan <strong className="text-accent-700">{fmtEUR(data.kostOpen)} open voor oplevering</strong>)</>}
          {' · '}offertewaarde {data.offertewaarde != null ? fmtEUR(data.offertewaarde) : '—'}
          {' · '}gefactureerd {data.gefactureerd != null ? fmtEUR(data.gefactureerd) : 'nog niets'}
          {data.voorlopig && ' · voorlopig: bevat een regel uit een calculatie die nog niet verstuurd is'}
        </p>
      )}
    </div>
  );
}
